import { showToast } from '@crowdex/ui/components/toaster';
import { useDockedApp } from '@/features/apps/app-panel-sidebar';
import { appView, useAppHome } from '@/lib/app-home';
import { type ShellApp, useShellApps } from './shell-navigation';
import {
  addFavorite,
  type FavoritesDensity,
  LIBRARY_FAVORITE,
  moveFavorite,
  removeFavorite,
  resolveFavorites,
  setFavoriteColumns,
  useAppUsage,
  useFavoritesLayout,
} from './sidebar-preferences';

/*
 * The favourites: the tiles at the top of the sidebar, of the phone's drawer and of the rail.
 * The user chooses which (Library, any app), their order and how many a row holds, right on the
 * tiles (drag, right-click) and from All apps (drag, pin); All apps is not one of them: it is
 * always the last tile, after them.
 */

/** One favourite tile, in order. `entry` is what the layout stores (`library`, an app's view). */
export type FavoriteItem =
  | { entry: typeof LIBRARY_FAVORITE; kind: 'library'; title: 'Library' }
  | { entry: string; kind: 'app'; title: string; app: ShellApp };

/**
 * The favourites shown on this device, how to open and change them. `items` excludes All apps,
 * which every surface shows after them; `columns` is the tiles of a row on each density.
 */
/** The favourites as last shown (what an Undo restores a tile among). */
let shownEntries: readonly string[] | null = null;

export function useFavorites() {
  const { apps, defaultPins, openApp } = useShellApps();
  const layout = useFavoritesLayout();
  const usage = useAppUsage();
  const enabled = apps.filter((app) => !app.disabled);
  const entries = resolveFavorites(
    enabled.map((app) => app.view),
    layout.entries,
    usage,
    defaultPins,
  );
  // What the favourites hold now: an Undo puts its tile back among them, whatever changed since.
  shownEntries = entries;
  const items = entries.flatMap((entry): FavoriteItem[] => {
    if (entry === LIBRARY_FAVORITE) return [{ entry, kind: 'library', title: 'Library' }];
    const app = enabled.find((candidate) => candidate.view === entry);
    return app === undefined ? [] : [{ entry, kind: 'app', title: app.title, app }];
  });
  return {
    items,
    /** Every app (the disabled ones too, which cannot be added). */
    apps,
    columns: layout.columns,
    isFavorite: (entry: string) => entries.includes(entry),
    add: (entry: string) => addFavorite(entries, entry),
    remove: (entry: string) => removeFavorite(entries, entry),
    /** Moves a favourite to `index` among them. */
    move: (entry: string, index: number) => moveFavorite(entries, entry, index),
    /** Adds an entry at `index` (moves it there when it is one already). */
    insert: (entry: string, index: number) =>
      moveFavorite(addFavorite(entries, entry), entry, index),
    /** Takes a tile out, saying so with an Undo that puts it back in its place. */
    unpin: (entry: string) => {
      const index = entries.indexOf(entry);
      if (index === -1) return;
      const title = items.find((item) => item.entry === entry)?.title ?? 'Favourite';
      const next = removeFavorite(entries, entry);
      showToast({
        title: `${title} removed`,
        tone: 'info',
        action: {
          label: 'Undo',
          // Only that tile comes back, at its former place among the favourites as they are now.
          onSelect: () => {
            const now = shownEntries ?? next;
            if (!now.includes(entry)) moveFavorite(addFavorite(now, entry), entry, index);
          },
        },
      });
    },
    setColumns: (density: FavoritesDensity, columns: number) =>
      setFavoriteColumns(density, columns),
    openApp,
  };
}

/** The favourites and what changes them, as `useFavorites` returns them. */
export type Favorites = ReturnType<typeof useFavorites>;

/**
 * The app whose tile is current: the one whose home is on screen (outside a conversation), else
 * the one docked as the active tab of the conversation's panel.
 */
export function useAppOnScreen(): string | null {
  const home = useAppHome();
  const docked = useDockedApp();
  return home !== null ? appView(home.plugin) : (docked?.view ?? null);
}
